import { Users } from '@platform/client';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import PageFooter from './layout/PageFooter';
import AuthedNavbar from './layout/AuthedNavbar';
import DashboardHeader from './layout/DashboardHeader';
import { UnauthedNavbar } from './layout/UnauthedNavbar';
import MetaTags from './util/MetaTags';

/** A route wrapper that redirects users to the login page if they are not authed */
export const AuthedPage = ({ children }: { children: React.ReactElement }) => {
  const userData = Users.useGetCurrentUser();
  const isAuthed = userData.isSuccess;
  const isLoading = userData.isLoading;
  const [location, navigate] = useLocation();

  useEffect(() => {
    if (!isAuthed && !isLoading) {
      navigate(`/auth/login?next=${location}`, { replace: true });
    }
  }, [isAuthed, navigate, location, isLoading]);

  return <>{children}</>;
};

export const HeaderFooterLayout = ({
  useDashboardHeader,
  children,
}: {
  useDashboardHeader?: boolean;
  children: ReactNode;
}) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const userData = Users.useGetCurrentUser();
  const isAuthed = userData.isSuccess;

  useEffect(() => {
    const hideSidebar = (e: MouseEvent) => {
      if (isSidebarOpen && !e.composedPath().includes(sidebarRef.current as HTMLDivElement)) {
        setIsSidebarOpen(false);
      }
    };

    document.addEventListener('click', hideSidebar, true);
    return () => document.removeEventListener('click', hideSidebar, true);
  }, [isSidebarOpen]);

  return (
    <>
      {!isAuthed && (
        <UnauthedNavbar
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          sidebarRef={sidebarRef}
        />
      )}
      {isAuthed && !useDashboardHeader && (
        <AuthedNavbar
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          sidebarRef={sidebarRef}
          showDashboardDropdown={!useDashboardHeader}
        />
      )}
      {isAuthed && useDashboardHeader && (
        <div className='hidden md:block'>
          <DashboardHeader />
        </div>
      )}
      <div className='flex flex-col overflow-x-clip overflow-y-auto h-[calc(100vh-54px)] md:h-[calc(100vh-var(--navbar-height))]'>
        <div className='flex-grow bg-gray-25 md:pt-12'>{children}</div>
        <div className='mt-auto'>
          <PageFooter />
        </div>
      </div>
    </>
  );
};

const App = ({ children }: { children: ReactNode }) => {
  return (
    <div>
      <MetaTags
        title='MovingWorlds - The Global Experteering Network'
        description='Find the best place to volunteer your skills, anywhere you want to travel.'
        imageUrl='https://assets.movingworlds.org/images/mwlogo-square-blue.png'
      />
      {children}
    </div>
  );
};

export default App;
